// src/components/ITINApplication.js
// ITIN Application UI — W-7 package generated and emailed
//
// DIFFERENT from EIN and BOI:
//   - No fax — IRS requires physical mail for W-7
//   - UI clearly explains what founder needs to do after receiving package
//   - Shows mailing address + checklist on confirmation screen
//   - Offers PDF download as backup
//
// Wired through useFilingGenerator(planTracker) — same hook as 5472,
// so credit checking / usage tracking is shared instead of local.
// ITIN's second PDF (the mailing-checklist cover letter) rides along on
// faxConfirmation.coverBase64 — the hook only keeps one "primary" PDF
// (generatedPdfBase64), so the extra one is tucked onto the confirmation
// object instead of inventing new hook state.
//
// UPDATED: reasonCode is hardcoded to 'h' (Exception) — every real user of
// this product is a foreign-owned single-member LLC owner applying under
// the exception category, not someone personally filing a 1040 (reason a)
// or claiming a treaty benefit (reason b). Removed the reason dropdown
// entirely; reasonText is now always required and always shown, matching
// backend validation in itin.js (which already defaults reasonCode to 'h'
// and requires reasonText whenever it is 'h'). `sex` (line 5 on the W-7,
// required per IRS instructions) is unchanged.
//
// UPDATED (this pass): replaced the free-typed "describe your reason" box
// with an Exception picker. Users were typing arbitrary descriptions
// (e.g. "LLC member requiring ITIN for tax withholding reporting") that
// don't match any IRS-recognized exception category — IRS instructions
// require the box h text to identify the exception "by its number, alpha
// subsection, and category" (e.g. "Exception 1(a)-Partnership Income").
// EXCEPTIONS below covers the two categories that actually apply to this
// product's users (source: IRS Form W-7 instructions, Exceptions Tables 1
// and 2 — irs.gov/instructions/iw7):
//   - 1(a): partner in a US/foreign partnership (LLC taxed as one) whose
//     assets generate income subject to IRS withholding/reporting
//   - 1(d): receiving distributions (dividends, royalties, rental income,
//     etc.) where a withholding agent needs an ITIN for reporting
// "Other" is kept as an escape hatch with the original free-text field for
// anyone who doesn't fit either — required in that case, same as before.
import React, { useState, useEffect } from 'react';
import useFilingGenerator from '../hooks/useFilingGenerator';
import { FILING_STATUS } from '../hooks/usePlanTracker';

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Armenia','Australia','Austria',
  'Azerbaijan','Bahrain','Bangladesh','Belarus','Belgium','Bolivia','Brazil',
  'Bulgaria','Canada','Chile','China','Colombia','Croatia','Czech Republic',
  'Denmark','Egypt','Finland','France','Georgia','Germany','Ghana','Greece',
  'Hungary','India','Indonesia','Iran','Iraq','Ireland','Israel','Italy',
  'Japan','Jordan','Kazakhstan','Kenya','Kuwait','Latvia','Lebanon','Lithuania',
  'Malaysia','Mexico','Morocco','Netherlands','New Zealand','Nigeria','Norway',
  'Pakistan','Panama','Peru','Philippines','Poland','Portugal','Qatar','Romania',
  'Russia','Saudi Arabia','Serbia','Singapore','South Africa','South Korea',
  'Spain','Sri Lanka','Sweden','Switzerland','Taiwan','Thailand','Turkey',
  'Ukraine','United Arab Emirates','United Kingdom','Uruguay','Uzbekistan',
  'Venezuela','Vietnam',
];

// ─── EXCEPTION PICKER ────────────────────────────────────────────────────
// value        → stored in form.exceptionType, drives which UI shows
// irsText      → the exact IRS-recognized string that goes on the W-7's
//                box h dotted line (per IRS instructions' own examples)
// hint         → shown under the dropdown to help the user pick correctly
// needsDetail  → whether the free-text field is required (true) or just an
//                optional add-on appended after irsText (false)
// NOTE: 1(a) ("partner in a partnership") is deliberately NOT offered here —
// every LLC this product handles is single-member/disregarded (see ein.js,
// which hardcodes "1 member"), and a single-member LLC has no "partners",
// so 1(a) doesn't actually apply to this product's users despite the
// similar-sounding name. Don't add it back without re-checking eligibility.
const EXCEPTIONS = [
  {
    value: '1b',
    label: 'Exception 1(b) — Opened a US business bank account for the LLC',
    irsText: 'Exception 1(b)-Bank Deposit Interest Income',
    hint: 'This is the common case: a US bank needs your ITIN to open the account and report/withhold on any interest it pays.',
    needsDetail: false,
  },
  {
    value: '1d',
    label: 'Exception 1(d) — Receiving distributions (dividends, royalties, rental, etc.)',
    irsText: 'Exception 1(d)-Distribution Income',
    hint: 'Pick this instead if a different withholding agent (not your bank) needs your ITIN to report or withhold on distributions paid to you.',
    needsDetail: false,
  },
  {
    value: 'other',
    label: "Other / not sure — I'll describe it myself",
    irsText: '',
    hint: 'Only use this if neither option above fits. Describe your situation as specifically as you can.',
    needsDetail: true,
  },
];

const EMPTY = {
  firstName: '', lastName: '', dateOfBirth: '',
  countryOfBirth: '', country: '',
  foreignTaxId: '',
  passportNumber: '', passportCountry: '', passportExpiryDate: '',
  address: '', city: '', stateProvince: '', postalCode: '',
  ownerEmail: '', llcName: '',
  reasonCode: 'h',
  exceptionType: '1b', // pre-selected: covers the near-universal case, still editable
  reasonText: '',
  sex: '',
};

export function ITINApplication({ planTracker, loadedRecord, onClearLoadedRecord }) {
  const {
    isProcessing,
    processingError,
    faxConfirmation,
    generatedPdfBase64,
    submitTool,
    downloadPDF,
    clearAll,
  } = useFilingGenerator(planTracker);

  const [form, setForm] = useState(EMPTY);

  // A fresh submission always wins over a loaded historical one. Note:
  // the mailing-checklist coverBase64 already rides along inside
  // faxConfirmation (see useFilingGenerator.submitTool), and that's exactly
  // how recordToolFiling stored it too, so this one field covers both PDFs.
  const confirmation = faxConfirmation || loadedRecord?.faxConfirmation || null;
  const pdfBase64     = generatedPdfBase64 || loadedRecord?.pdfBase64 || null;

  useEffect(() => {
    if (loadedRecord?.formData) setForm({ ...EMPTY, ...loadedRecord.formData, reasonCode: 'h' });
  }, [loadedRecord]);

  const handle = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const selectedException = EXCEPTIONS.find(ex => ex.value === form.exceptionType) || null;

  const handleSubmit = async () => {
    // Build the exact box-h text sent to the backend: the IRS-standard
    // label for 1a/1d (optionally with the user's extra detail appended),
    // or the user's own free text when they picked "Other".
    const detail = form.reasonText.trim();
    const resolvedReasonText = !selectedException ? ''
      : selectedException.needsDetail
        ? detail
        : (detail ? `${selectedException.irsText} - ${detail}` : selectedException.irsText);

    try {
      await submitTool('/api/itin', { ...form, reasonText: resolvedReasonText }, {
        type:   'itin',
        status: FILING_STATUS.FILED,
        buildTitle: (p) => `${p.firstName || ''} ${p.lastName || ''}`.trim()
          ? `${p.firstName} ${p.lastName} — ITIN Application`
          : 'ITIN Application',
      });
    } catch {
      // processingError is already set by the hook
    }
  };

  const handleNew = () => {
    clearAll();
    setForm(EMPTY);
    onClearLoadedRecord?.();
  };

  const isValid =
    form.firstName && form.lastName && form.ownerEmail &&
    form.dateOfBirth && form.country &&
    form.passportNumber && form.passportCountry &&
    form.sex &&
    form.exceptionType &&
    (!selectedException?.needsDetail || form.reasonText.trim());

  // ── CONFIRMATION SCREEN ───────────────────────────────────────────────────
  // Renders for a fresh submission AND when reopened from sidebar history.
  if (confirmation) {
    const addr = confirmation.mailingAddress;
    return (
      <div className="form-page">
        <div className="form-page-inner">

          <div className="fr-status-card fr-status-card--confirmed" style={{ marginBottom: 20 }}>
            <div className="fr-status-icon fr-status-icon--confirmed">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="fr-status-text">
              <div className="fr-status-headline">ITIN Package Ready — Sent to {confirmation.sentTo}</div>
              <div className="fr-status-sub">
                W-7 pre-filled · Mailing checklist included · Processing: {confirmation.processingTime}
              </div>
            </div>
          </div>

          {/* Warning banner — explains what founder still needs to do */}
          <div style={{
            background: '#FEF3C7', border: '1px solid #F59E0B',
            borderRadius: 8, padding: '14px 18px', marginBottom: 20,
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 2 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="9" x2="12" y2="13" stroke="#D97706" strokeWidth="2" strokeLinecap="round"/>
              <line x1="12" y1="17" x2="12.01" y2="17" stroke="#D97706" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div>
              <strong style={{ color: '#92400E' }}>You need to mail this package yourself.</strong>
              <p style={{ margin: '4px 0 0', color: '#78350F', fontSize: 13 }}>
                W-7 cannot be faxed or submitted digitally — IRS requires original certified documents by mail.
                Your package is pre-filled and ready. Print it, add your certified passport copy, and mail it.
              </p>
            </div>
          </div>

          <div className="fr-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div>
              <div className="fr-card">
                <div className="fr-card-title">Next Steps — Do These Now</div>
                <div className="fr-next-list">
                  {(confirmation.nextSteps || []).map((step, i) => (
                    <div className="fr-next-item" key={i}>
                      <div className="fr-next-num">{i + 1}</div>
                      <div className="fr-next-text">{step}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="fr-actions" style={{ marginTop: 16, flexDirection: 'column' }}>
                {pdfBase64 && (
                  <button className="fr-btn-primary"
                    onClick={() => downloadPDF(
                      pdfBase64,
                      `W-7-ITIN-${form.firstName}-${form.lastName}.pdf`
                    )}
                    type="button">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Download W-7 PDF
                  </button>
                )}
                {confirmation.coverBase64 && (
                  <button className="fr-btn-secondary" style={{ marginTop: 8 }}
                    onClick={() => downloadPDF(confirmation.coverBase64, 'ITIN-Mailing-Checklist.pdf')}
                    type="button">
                    Download Mailing Checklist
                  </button>
                )}
                <button className="fr-btn-secondary" style={{ marginTop: 8 }}
                  onClick={handleNew}
                  type="button">
                  New Application
                </button>
              </div>
            </div>

            <div>
              <div className="fr-card">
                <div className="fr-card-title">IRS Mailing Address</div>
                <div style={{ padding: '12px 0', lineHeight: 1.8, fontSize: 14 }}>
                  {addr && Object.values(addr).map((line, i) => (
                    <div key={i} style={{ fontWeight: i === 0 ? 600 : 400 }}>{line}</div>
                  ))}
                </div>
                <p className="fr-note">
                  Use registered/tracked international mail. Keep your tracking number.
                </p>
              </div>

              <div className="fr-card" style={{ marginTop: 16 }}>
                <div className="fr-card-title">Filing Details</div>
                <div className="fr-detail-list">
                  {[
                    ['Name',        confirmation.fullName],
                    ['Email sent',  confirmation.sentTo],
                    ['Processing',  confirmation.processingTime],
                    ['LLC',         confirmation.llcName || 'N/A'],
                    ['Prepared',    new Date(confirmation.preparedAt).toLocaleDateString()],
                  ].map(([label, value]) => (
                    <div className="fr-detail-row" key={label}>
                      <span className="fr-detail-label">{label}</span>
                      <span className="fr-detail-value">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  // ── FORM ──────────────────────────────────────────────────────────────────
  return (
    <div className="form-page">
      <div className="form-page-inner">

        <div className="form-hero">
          <div className="form-hero-left">
            <div className="form-eyebrow">
              <span className="eyebrow-pulse" />
              IRS Form W-7 · ITIN Application
            </div>
            <h1 className="form-title">
              Get your ITIN without <em>the 6-month nightmare</em>
            </h1>
            <p className="form-subtitle">
              We pre-fill your W-7 and send you a complete mailing package.
              You just print, sign, and mail with your certified passport copy.
            </p>
          </div>
        </div>

        <div className="form-top-grid">

          {/* Personal Info Panel */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">Personal Information</span>
            </div>
            <div className="form-panel-body">

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">First name</label>
                  <input className="form-input" type="text"
                    placeholder="Jan"
                    value={form.firstName} onChange={handle('firstName')}
                    disabled={isProcessing} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last name</label>
                  <input className="form-input" type="text"
                    placeholder="Müller"
                    value={form.lastName} onChange={handle('lastName')}
                    disabled={isProcessing} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email"
                  placeholder="you@example.com"
                  value={form.ownerEmail} onChange={handle('ownerEmail')}
                  disabled={isProcessing} />
                <span className="form-field-hint">We'll email your complete W-7 package here</span>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Date of birth</label>
                  <input className="form-input" type="date"
                    value={form.dateOfBirth} onChange={handle('dateOfBirth')}
                    disabled={isProcessing} />
                </div>
                <div className="form-group">
                  <label className="form-label">Country of birth</label>
                  <select className="form-input form-select"
                    value={form.countryOfBirth} onChange={handle('countryOfBirth')}
                    disabled={isProcessing}>
                    <option value="">Select country</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Sex — line 5 on the W-7, required per IRS instructions */}
              <div className="form-group">
                <label className="form-label">Sex</label>
                <select className="form-input form-select"
                  value={form.sex} onChange={handle('sex')}
                  disabled={isProcessing}>
                  <option value="">Select</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Country of citizenship</label>
                <select className="form-input form-select"
                  value={form.country} onChange={handle('country')}
                  disabled={isProcessing}>
                  <option value="">Select country</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Foreign tax ID (optional)</label>
                <input className="form-input" type="text"
                  placeholder="Your country's tax ID number"
                  value={form.foreignTaxId} onChange={handle('foreignTaxId')}
                  disabled={isProcessing} />
                <span className="form-field-hint">If your country issues a personal tax ID, enter it here</span>
              </div>

              {/* Exception picker — required by the IRS on the dotted line
                  next to box h. Every user of this product applies under
                  the exception category (reasonCode is fixed to 'h'), so
                  this is always shown and always required. Replaces the
                  old free-text box: IRS instructions require box h to
                  identify the exception "by its number, alpha subsection,
                  and category" — a typed description alone isn't valid. */}
              <div className="form-group">
                <label className="form-label">Which exception applies to you? (required)</label>
                <select className="form-input form-select"
                  value={form.exceptionType} onChange={handle('exceptionType')}
                  disabled={isProcessing}>
                  <option value="">Select an exception</option>
                  {EXCEPTIONS.map(ex => (
                    <option key={ex.value} value={ex.value}>{ex.label}</option>
                  ))}
                </select>
                {selectedException && (
                  <span className="form-field-hint">{selectedException.hint}</span>
                )}
              </div>

              {selectedException && (
                <div className="form-group">
                  <label className="form-label">
                    {selectedException.needsDetail
                      ? 'Describe your situation (required)'
                      : 'Additional detail (optional)'}
                  </label>
                  <input className="form-input" type="text"
                    placeholder={selectedException.needsDetail
                      ? 'Briefly describe why you need the ITIN'
                      : 'e.g. rental income from a US property owned by the LLC'}
                    value={form.reasonText} onChange={handle('reasonText')}
                    disabled={isProcessing} />
                  <span className="form-field-hint">
                    {selectedException.needsDetail
                      ? "This goes on the dotted line next to box h on your W-7 — be as specific as you can."
                      : `We'll print "${selectedException.irsText}" on the dotted line next to box h${form.reasonText.trim() ? `, followed by "${form.reasonText.trim()}"` : ''}.`}
                  </span>
                </div>
              )}

            </div>
          </div>

          {/* Passport + Address Panel */}
          <div className="form-panel">
            <div className="form-panel-header">
              <span className="form-panel-dot" />
              <span className="form-panel-title">Passport & Address</span>
            </div>
            <div className="form-panel-body">

              <div className="form-group">
                <label className="form-label">Passport number</label>
                <input className="form-input" type="text"
                  placeholder="AB1234567"
                  value={form.passportNumber} onChange={handle('passportNumber')}
                  disabled={isProcessing} />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Passport country</label>
                  <select className="form-input form-select"
                    value={form.passportCountry} onChange={handle('passportCountry')}
                    disabled={isProcessing}>
                    <option value="">Select country</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Passport expiry date</label>
                  <input className="form-input" type="date"
                    value={form.passportExpiryDate} onChange={handle('passportExpiryDate')}
                    disabled={isProcessing} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Street address</label>
                <input className="form-input" type="text"
                  placeholder="Unter den Linden 12"
                  value={form.address} onChange={handle('address')}
                  disabled={isProcessing} />
              </div>

              <div className="form-row-3-col">
                <div className="form-group">
                  <label className="form-label">City</label>
                  <input className="form-input" type="text"
                    value={form.city} onChange={handle('city')}
                    disabled={isProcessing} />
                </div>
                <div className="form-group">
                  <label className="form-label">State/Province</label>
                  <input className="form-input" type="text"
                    value={form.stateProvince} onChange={handle('stateProvince')}
                    disabled={isProcessing} />
                </div>
                <div className="form-group">
                  <label className="form-label">Postal code</label>
                  <input className="form-input" type="text"
                    value={form.postalCode} onChange={handle('postalCode')}
                    disabled={isProcessing} />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">LLC name (optional)</label>
                <input className="form-input" type="text"
                  placeholder="Acme Solutions LLC"
                  value={form.llcName} onChange={handle('llcName')}
                  disabled={isProcessing} />
                <span className="form-field-hint">If applying in connection with your LLC</span>
              </div>

            </div>
          </div>
        </div>

        <div className="form-footer-bar">
          <div className="form-footer-left">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            W-7 pre-filled · Complete mailing package emailed to you
          </div>
          <button
            className="btn-generate"
            onClick={handleSubmit}
            disabled={isProcessing || !isValid}
            type="button"
          >
            {isProcessing ? (
              <><div className="spinner-small" /> Preparing package…</>
            ) : (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Prepare ITIN Package
              </>
            )}
          </button>
        </div>

        {processingError && (
          <div className="error-message">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {processingError}
          </div>
        )}

      </div>
    </div>
  );
}